import {
  DEFAULT_MEASUREMENT,
  DEFAULT_MEASUREMENT_VALIDATION_RESULT,
  Measurement,
} from 'constants/MeasurementConstants';
import {DataFrame, DataFrameRow} from 'data-frames/DataFrameTypes';
import {PlateRecordHydratedDataQueryVariables} from 'samples/plate-maps/plate-record/__generated/PlateRecordHydratedDataQuery';
import {UnitsById} from 'samples/plate-maps/plate-record/PlateRecordUtil';
import {
  BioentityContent,
  INACCESSIBLE_GROUP_ID_SENTINEL,
  PlateMap,
  ReagentType,
  Source,
} from 'samples/plate-maps/PlateMapConstants';
import {getFlattenedWellIndex} from 'samples/plate-maps/PlateMapUtil';

export const CONTENT_LAYER_NAME = 'content';

export interface ContentConcentration {
  amount: number;
  unit: {apiId: string; versionNumber: number | null} | null;
}

export interface ContentPlacement {
  entityApiId: string;
  replicateIndex: number | null;
  fillGroupIndex: number | null;
  concentration: ContentConcentration | null;
}

export type ContentByWellPosition = Map<string, Array<ContentPlacement>>;

export interface ParsedContent {
  contentByWellPosition: ContentByWellPosition;
  // True when at least one source row carried a `_sample_name` without an `_entity_api_id`.
  hasUnregisteredSamples: boolean;
  // True when the backend has more rows than were fetched. The viewer should
  // surface this rather than render a partial plate.
  isTruncated: boolean;
}

export function contentWellPositionKey(rowIndex: number, columnIndex: number): string {
  return `${rowIndex},${columnIndex}`;
}

function asNullableString(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  return typeof value === 'string' ? value : String(value);
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parsePlacement(row: DataFrameRow): ContentPlacement | null {
  const entityApiId = asNullableString(row._entity_api_id);

  // Empty-well placeholders and named-only rows are skipped; named-only rows
  // are flagged separately on `ParsedContent.hasUnregisteredSamples`.
  if (entityApiId == null) {
    return null;
  }

  const amount = asNullableNumber(row._concentration_amount);
  const unitApiId = asNullableString(row._concentration_unit_api_id);
  const unitVersionNumber = asNullableNumber(row._concentration_unit_version_number);

  const concentration: ContentConcentration | null =
    amount != null
      ? {
          amount,
          unit: unitApiId != null ? {apiId: unitApiId, versionNumber: unitVersionNumber} : null,
        }
      : null;

  return {
    entityApiId,
    replicateIndex: asNullableNumber(row._replicate_index),
    fillGroupIndex: asNullableNumber(row._fill_group_index),
    concentration,
  };
}

interface BuildFilledPlateMapInput {
  // Base plate map from `ScratchPlateDesignSummaryUtil.buildPlateMap`; carries
  // everything except per-well `contents`.
  basePlateMap: PlateMap;
  contentByWellPosition: ContentByWellPosition;
  // Bioentity API id → its storable schema API id. Missing entries fall back
  // to `INACCESSIBLE_GROUP_ID_SENTINEL` so the "(No access)" chip renders.
  schemaIdsByReagentId?: {[id: string]: string | undefined};
  // Hydrated `MeasurementUnit` keyed by `(apiId, versionNumber)`. Missing
  // entries leave the well concentration rendered as a bare number.
  unitsById?: UnitsById;
}

function buildMeasurement(
  concentration: ContentConcentration | null,
  unitsById: UnitsById
): Measurement | null {
  if (concentration == null) {
    return null;
  }
  const unitNode = concentration.unit;
  const hydratedUnit =
    unitNode != null ? (unitsById[unitNode.apiId]?.[unitNode.versionNumber ?? 1] ?? null) : null;
  return {value: concentration.amount, unit: hydratedUnit};
}

function buildContent(
  placement: ContentPlacement,
  schemaIdsByReagentId: {[id: string]: string | undefined},
  unitsById: UnitsById
): BioentityContent {
  const measurement = buildMeasurement(placement.concentration, unitsById);
  // No sources are transferred into a filled scratch plate, so finalConcentration
  // mirrors initial.
  const finalConcentration = {
    measurement: measurement ?? DEFAULT_MEASUREMENT,
    errors: DEFAULT_MEASUREMENT_VALIDATION_RESULT,
  };

  return {
    type: ReagentType.WELL_CONTENT,
    id: placement.entityApiId,
    groupId: schemaIdsByReagentId[placement.entityApiId] ?? INACCESSIBLE_GROUP_ID_SENTINEL,
    initialConcentration: measurement,
    finalConcentration,
  };
}

/**
 * Fold per-well `contents` onto a base `PlateMap`. Other well fields
 * (role, schema tags, concentration layers) pass through untouched.
 */
export function buildFilledPlateMap(input: BuildFilledPlateMapInput): PlateMap {
  const {basePlateMap, contentByWellPosition, schemaIdsByReagentId = {}, unitsById = {}} = input;

  return {
    ...basePlateMap,
    wells: basePlateMap.wells.map((well) => {
      const wellKey = contentWellPositionKey(well.position.row, well.position.column);
      const placements = contentByWellPosition.get(wellKey) ?? [];
      if (placements.length === 0) {
        return well;
      }
      const contents = placements.map((placement) =>
        buildContent(placement, schemaIdsByReagentId, unitsById)
      );
      return {...well, contents};
    }),
  };
}

/**
 * Group the rows of a filled scratch plate's `content` layer by well position;
 * placements appear in source-row order. `totalRowCount` is the data frame's
 * authoritative row count (e.g. `DataFrameNode.numRows`); when it exceeds the
 * number of rows in `contentDataFrame`, the result is flagged as truncated.
 */
export function parseContentDataFrame(contentDataFrame: DataFrame, totalRowCount: number): ParsedContent {
  const contentByWellPosition: ContentByWellPosition = new Map();
  let hasUnregisteredSamples = false;
  for (const row of contentDataFrame.rows) {
    const entityApiId = asNullableString(row._entity_api_id);
    if (entityApiId == null) {
      if (asNullableString(row._sample_name) != null) {
        hasUnregisteredSamples = true;
      }
      continue;
    }
    const placement = parsePlacement(row);
    if (placement == null) {
      continue;
    }
    const rowIndex = asNullableNumber(row._row_index);
    const columnIndex = asNullableNumber(row._column_index);
    if (rowIndex == null || columnIndex == null) {
      continue;
    }
    const key = contentWellPositionKey(rowIndex, columnIndex);
    const existing = contentByWellPosition.get(key);
    if (existing != null) {
      existing.push(placement);
    } else {
      contentByWellPosition.set(key, [placement]);
    }
  }
  return {
    contentByWellPosition,
    hasUnregisteredSamples,
    isTruncated: totalRowCount > contentDataFrame.rows.length,
  };
}

/**
 * Derive the variables needed to bulk-hydrate the bioentities and concentration
 * units referenced by a parsed content layer. Missing unit version numbers
 * fall back to `1`, matching the convention in the rest of the plate stack.
 */
export function buildHydrationVariablesFromContent(
  contentByWellPosition: ContentByWellPosition
): PlateRecordHydratedDataQueryVariables {
  const bioentityIds = new Set<string>();
  const unitVariants = new Map<string, {apiId: string; versionNumber: number}>();
  for (const placements of contentByWellPosition.values()) {
    for (const placement of placements) {
      bioentityIds.add(placement.entityApiId);
      const unit = placement.concentration?.unit;
      if (unit != null) {
        const versionNumber = unit.versionNumber ?? 1;
        unitVariants.set(`${unit.apiId}#${versionNumber}`, {apiId: unit.apiId, versionNumber});
      }
    }
  }
  const variants = [...unitVariants.values()];
  return {
    bioentityIds: [...bioentityIds],
    storableIds: [],
    unitIds: variants.map((v) => v.apiId),
    unitVersionNumbers: variants.map((v) => v.versionNumber),
    testOrderIds: [],
  };
}

export interface FilledScratchPlateData {
  plateName: string | null;
  plateMap: PlateMap;
  containerSchemaId: string | null;
  // True when the content data frame had more rows on the backend than were
  // fetched, so the plate map is built from a partial set of placements.
  isTruncated: boolean;
}

export function bioentitySourceToContent(source: Source): BioentityContent {
  const measurement: Measurement | null =
    source.type === ReagentType.SOURCE_CONTAINABLE ? (source.finalConcentration?.measurement ?? null) : null;

  const finalConcentration =
    source.type === ReagentType.SOURCE_CONTAINABLE && source.finalConcentration != null
      ? source.finalConcentration
      : {measurement: measurement ?? DEFAULT_MEASUREMENT, errors: DEFAULT_MEASUREMENT_VALIDATION_RESULT};

  return {
    type: ReagentType.WELL_CONTENT,
    id: source.id,
    groupId: source.groupId !== '' ? source.groupId : INACCESSIBLE_GROUP_ID_SENTINEL,
    initialConcentration: measurement,
    finalConcentration,
  };
}

export function buildFilledPlateMapFromResolvedSources(
  basePlateMap: PlateMap,
  resolvedPlate: {[wellIndex: number]: Array<Source>}
): PlateMap {
  return {
    ...basePlateMap,
    wells: basePlateMap.wells.map((well) => {
      const wellIndex = getFlattenedWellIndex(well.position.row, well.position.column, basePlateMap.size);
      const sources = resolvedPlate[wellIndex];
      if (sources == null || sources.length === 0) {
        return well;
      }
      return {
        ...well,
        sources: [],
        contents: sources.map((source) => bioentitySourceToContent(source)),
      };
    }),
  };
}
